import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CapacityConflict } from '@obraplan/core/domain/planning/allocation-conflict';
import { MIN_JUSTIFICATION_LENGTH } from '@obraplan/core/domain/planning/over-capacity-policy';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { Button } from '@/components/ui/button';
import { Field, Textarea } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatBusinessDate } from '@/lib/format';

/**
 * A exceção de capacidade, na tela (RN-045).
 *
 * O diálogo mostra os três números — capacidade, planejado e excedente — porque
 * autorizar um excesso sem saber o tamanho dele não é autorizar, é clicar. E exige a
 * justificativa que vai ficar no registro e na auditoria.
 *
 * Para quem **não** tem a permissão, não existe campo nenhum: existe uma recusa que
 * explica o que aconteceu e a quem pedir. Esconder o botão deixaria a pessoa achando que
 * o sistema travou.
 *
 * Este diálogo já teve um segundo modo, para a justificativa da segunda alteração do dia
 * no planejamento. Ele não existe mais: **planejar não presta contas**. Enquanto o dia não
 * aconteceu, remanejar gente é o trabalho sendo feito, e o único motivo que o planejamento
 * ainda pede é o de estourar a capacidade — que é o que este diálogo faz. Explicar
 * mudança é assunto da conferência, e lá o diálogo é outro, com os números do dia à vista.
 */
export interface CapacityOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflict: CapacityConflict | undefined;
  resourceName: string;
  canOverride: boolean;
  isPending: boolean;
  errorMessage?: string;
  onConfirm: (justification: string) => void;
}

export function CapacityOverrideDialog({
  open,
  onOpenChange,
  conflict,
  resourceName,
  canOverride,
  isPending,
  errorMessage,
  onConfirm,
}: CapacityOverrideDialogProps) {
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (open) setJustification('');
  }, [open]);

  const canWrite = canOverride;
  const tooShort = justification.trim().length < MIN_JUSTIFICATION_LENGTH;

  const periodLabel =
    conflict === undefined
      ? 'do período'
      : conflict.from === conflict.to
        ? `de ${formatBusinessDate(conflict.from)}`
        : `de ${formatBusinessDate(conflict.from)} a ${formatBusinessDate(conflict.to)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="excesso-descricao">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-(--color-state-danger)">
            <AlertTriangle className="size-5" aria-hidden="true" />
            Capacidade excedida
          </DialogTitle>
          <DialogDescription id="excesso-descricao">
            {/*
              O período vem do conflito, não de uma suposição da tela: a verificação é do
              dia, mas a cópia de semana e os relatórios usam o mesmo diálogo com outro
              escopo. Escrever "da semana" fixo estaria errado na maioria das vezes.
            */}
            {`${resourceName} passa da jornada ${periodLabel} com esta alocação.`}
          </DialogDescription>
        </DialogHeader>

        {conflict !== undefined && (
          <dl className="grid grid-cols-3 gap-3 rounded-(--radius-card) border border-(--color-border-subtle) p-3 text-sm">
            <div>
              <dt className="text-xs text-(--color-content-muted)">Capacidade</dt>
              <dd className="tabular font-medium">
                {Hours.ofQuarters(conflict.capacityQuarterHours).format()}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-(--color-content-muted)">Planejado</dt>
              <dd className="tabular font-medium">
                {Hours.ofQuarters(conflict.plannedQuarterHours).format()}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-(--color-content-muted)">Excedente</dt>
              <dd className="tabular font-medium text-(--color-state-danger)">
                {Hours.ofQuarters(conflict.excessQuarterHours).format()}
              </dd>
            </div>
            <div className="col-span-3 text-xs text-(--color-content-muted)">
              {conflict.from === conflict.to
                ? `Dia: ${formatBusinessDate(conflict.from)}`
                : `Período: ${formatBusinessDate(conflict.from)} a ${formatBusinessDate(conflict.to)}`}
            </div>
          </dl>
        )}

        {canWrite ? (
          <div className="mt-4">
            <Field
              label="Justificativa"
              htmlFor="justificativa"
              required
              hint={`Fica registrada na alocação e na auditoria. Mínimo de ${String(MIN_JUSTIFICATION_LENGTH)} caracteres.`}
            >
              <Textarea
                id="justificativa"
                rows={3}
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
                placeholder="Ex.: virada de concretagem autorizada pelo gestor da obra."
              />
            </Field>
          </div>
        ) : (
          <div
            role="alert"
            className="mt-4 flex gap-2 rounded-(--radius-card) border border-(--color-state-warn) p-3 text-sm"
          >
            <ShieldAlert className="size-4 shrink-0 text-(--color-state-warn)" aria-hidden="true" />
            <p>
              Seu perfil não pode alocar acima da capacidade. Reduza as horas, ou peça a alguém com
              o papel de <strong>Gestor da obra</strong> para autorizar o excesso com justificativa.
            </p>
          </div>
        )}

        {errorMessage !== undefined && (
          <p role="alert" className="mt-3 text-sm text-(--color-state-danger)">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {canWrite ? 'Cancelar' : 'Entendi'}
          </Button>
          {canWrite && (
            <Button
              variant="danger"
              disabled={tooShort || isPending}
              onClick={() => onConfirm(justification.trim())}
            >
              Autorizar o excesso
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
